import { Col, Menu, Space, Tooltip } from 'antd';
import { LottieIcon } from 'components/common/LottieIcon';
import { useFormikContext } from 'formik';
import { useStepBarMode } from 'hooks/useStepBarMode';
import { SparingRequestPayload } from 'models/SparingRequests';
import { CSSProperties, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetSparingRequestConfigurationTypesQuery } from 'redux/services/calvin/sparingRequestConfigTypes';
import { setProductsView } from 'redux/slices/formSlice';
import { getDefectiveReturnsRequiredInfoErrors } from 'utils/defectiveReturns';
import { customerLottie, defectiveLottie, deliveryLottie, headerInfoLottie, otherLottie, outageLottie, productsLottie, reviewOrderLottie, serviceLottie } from '../../../assets/customer-lottie';
import './StepControls.css';

export enum CreateRequestMenu {
  RequestDetails = 'request-details',
  OutageLocation = 'outage-location',
  Products = 'products',
  ShippingLocation = 'shipping-location',
  RequestInfo = 'request-info',
  Contact = 'contact-info',
  ReviewOrder = 'review-order',
  ServiceLevel = 'service-level',
  DefectiveReturns = 'defective-returns'
}

interface StepDefinition {
  key: CreateRequestMenu;
  title: string;
  lottieFile: object;
  complete: boolean;
  required?: boolean;
  disabled?: boolean;
  /** Steps that have always been wrapped in Tooltip + Col in the full bar. */
  wrapped?: boolean;
  /** Why the step is unavailable. Only set while it is. */
  disabledReason?: string;
}

const stepItemStyle: CSSProperties = { paddingLeft: 10, paddingRight: 10, flex: 'none' };

// disabledOverflow drops antd's flex wrapper, so the row is restored here. It can only wrap once every step except the
// current one is an icon and the bar still does not fit (phone widths): until then useStepBarMode keeps it on one row.
const menuStyle: CSSProperties = { display: 'flex', justifyContent: 'center', flexWrap: 'wrap' };

// Holds the invisible measuring copies: exactly as wide as the bar may be, no height, clipped, hidden from assistive tech
// and the pointer, and after the real bar in DOM order.
const measureBoxStyle: CSSProperties = { position: 'absolute', top: 0, left: 0, width: '100%', height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' };

export const StepControls = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id: deploymentRequestId } = useParams();

  const { values } = useFormikContext<SparingRequestPayload>();
  const { data: requestTypesData } = useGetSparingRequestConfigurationTypesQuery();

  const isGeneralComplete = Boolean(values.sparingRequestTypeId);

  const isSlaValid = values.lineItems
    .map((line) => {
      if (!line?.subLines?.length) return false;
      const totalSublineQty = (line?.subLines ?? [])?.reduce((sum, curr) => sum + (curr.quantity ?? 0), 0);

      return totalSublineQty === line.quantity;
    })
    .every((item) => item);

  const validOutageLocationStep = values.outageLocationId;
  const validProductsStep = !!values.lineItems?.length;
  const validShippingLocationStep = values.shippingLocationId;
  const contactPhoneDigits = (values.contactPhoneNumber || '').replace(/\D/g, '');
  const isPhoneValid = contactPhoneDigits.length === 10;
  const validContactInfoStep = values.contactEmail !== '' && values.contactName !== '' && isPhoneValid;

  const selectedType = requestTypesData?.data?.find((t) => t.id === values.sparingRequestTypeId);
  const enableDefectiveReturns = useMemo(() => {
    return Boolean(selectedType?.isActivateDefectiveReturns);
  }, [selectedType?.isActivateDefectiveReturns]);

  // MDSI1-4304: while the requirement is active the step is a mandatory step like the others, and
  // "complete" means the shared helper is satisfied rather than merely that the tab is visible.
  const defectiveReturnsRequiredInfo = getDefectiveReturnsRequiredInfoErrors({
    items: values.defectiveReturns?.items,
    lineItems: values.lineItems,
    requestType: selectedType
  });

  const validDefectiveReturnsStep = defectiveReturnsRequiredInfo.isRequirementActive ? defectiveReturnsRequiredInfo.isValid : enableDefectiveReturns;

  const currentLocation = useMemo(() => {
    if (pathname.includes(CreateRequestMenu.OutageLocation)) {
      return CreateRequestMenu.OutageLocation;
    } else if (pathname.includes(CreateRequestMenu.Products)) {
      return CreateRequestMenu.Products;
    } else if (pathname.includes(CreateRequestMenu.ShippingLocation)) {
      return CreateRequestMenu.ShippingLocation;
    } else if (pathname.includes(CreateRequestMenu.RequestInfo)) {
      return CreateRequestMenu.RequestInfo;
    } else if (pathname.includes(CreateRequestMenu.ServiceLevel)) {
      return CreateRequestMenu.ServiceLevel;
    } else if (pathname.includes(CreateRequestMenu.DefectiveReturns)) {
      return CreateRequestMenu.DefectiveReturns;
    } else if (pathname.includes(CreateRequestMenu.Contact)) {
      return CreateRequestMenu.Contact;
    } else if (pathname.includes(CreateRequestMenu.ReviewOrder)) {
      return CreateRequestMenu.ReviewOrder;
    } else {
      return CreateRequestMenu.RequestDetails;
    }
  }, [pathname]);

  const handleNavigationCreate = (info: { key: string }): void => {
    if (info.key === CreateRequestMenu.RequestDetails) {
      navigate('/create');
    } else if (info.key === CreateRequestMenu.OutageLocation) {
      navigate('/create/outage-location');
    } else if (info.key === CreateRequestMenu.Products) {
      dispatch(setProductsView('grid'));
      navigate('/create/products');
    } else if (info.key === CreateRequestMenu.ShippingLocation) {
      navigate('/create/shipping-location');
    } else if (info.key === CreateRequestMenu.RequestInfo) {
      navigate('/create/request-info');
    } else if (info.key === CreateRequestMenu.ServiceLevel) {
      navigate('/create/service-level');
    } else if (info.key === CreateRequestMenu.DefectiveReturns) {
      navigate('/create/defective-returns');
    } else if (info.key === CreateRequestMenu.Contact) {
      navigate('/create/contact-info');
    } else if (info.key === CreateRequestMenu.ReviewOrder) {
      navigate('/create/review-order');
    }
  };
  const handleNavigationEdit = (info: { key: string }): void => {
    if (info.key === CreateRequestMenu.RequestDetails) {
      navigate(`/edit/${deploymentRequestId}`);
    } else if (info.key === CreateRequestMenu.OutageLocation) {
      navigate(`/edit/${deploymentRequestId}/outage-location`);
    } else if (info.key === CreateRequestMenu.Products) {
      dispatch(setProductsView('grid'));
      navigate(`/edit/${deploymentRequestId}/products`);
    } else if (info.key === CreateRequestMenu.ShippingLocation) {
      navigate(`/edit/${deploymentRequestId}/shipping-location`);
    } else if (info.key === CreateRequestMenu.DefectiveReturns) {
      navigate(`/edit/${deploymentRequestId}/defective-returns`);
    } else if (info.key === CreateRequestMenu.ServiceLevel) {
      navigate(`/edit/${deploymentRequestId}/service-level`);
    } else if (info.key === CreateRequestMenu.RequestInfo) {
      navigate(`/edit/${deploymentRequestId}/request-info`);
    } else if (info.key === CreateRequestMenu.Contact) {
      navigate(`/edit/${deploymentRequestId}/contact-info`);
    } else if (info.key === CreateRequestMenu.ReviewOrder) {
      navigate(`/edit/${deploymentRequestId}/review-order`);
    }
  };

  const arePrerequisitesMissing = !validShippingLocationStep || !isGeneralComplete;

  // One definition per step, in display order. The full bar, the compact bar and the invisible measuring copy are all
  // rendered from this list, so they can never disagree about which steps exist or how wide they are.
  const steps: StepDefinition[] = [
    { key: CreateRequestMenu.RequestDetails, title: 'Request Identification', lottieFile: headerInfoLottie, complete: isGeneralComplete, required: true },
    { key: CreateRequestMenu.OutageLocation, title: 'Outage Location', lottieFile: outageLottie, complete: Boolean(validOutageLocationStep), required: true },
    { key: CreateRequestMenu.ShippingLocation, title: 'Shipping Location', lottieFile: deliveryLottie, complete: Boolean(validShippingLocationStep), required: true },
    {
      key: CreateRequestMenu.Products,
      title: 'Products',
      lottieFile: productsLottie,
      complete: validProductsStep,
      required: true,
      disabled: arePrerequisitesMissing,
      wrapped: true,
      disabledReason: arePrerequisitesMissing ? 'Select a shipping address and sparing request type  to access this section.' : undefined
    },
    {
      key: CreateRequestMenu.ServiceLevel,
      title: 'Service Level',
      lottieFile: serviceLottie,
      complete: !!values.lineItems.length && isSlaValid,
      required: true,
      disabled: arePrerequisitesMissing,
      wrapped: true,
      disabledReason: arePrerequisitesMissing ? 'Select a shipping address and sparing request type to access this section.' : undefined
    },
    { key: CreateRequestMenu.Contact, title: 'Contact', lottieFile: customerLottie, complete: Boolean(validContactInfoStep), required: true },
    { key: CreateRequestMenu.RequestInfo, title: 'Other', lottieFile: otherLottie, complete: false }
  ];

  if (enableDefectiveReturns) {
    steps.push({
      key: CreateRequestMenu.DefectiveReturns,
      title: 'Defective Returns',
      lottieFile: defectiveLottie,
      complete: validDefectiveReturnsStep,
      required: defectiveReturnsRequiredInfo.isRequirementActive,
      wrapped: true
    });
  }

  steps.push({ key: CreateRequestMenu.ReviewOrder, title: 'Review Order', lottieFile: reviewOrderLottie, complete: false });

  const currentIndex = steps.findIndex((step) => step.key === currentLocation);
  // Everything that can change a step's width: which steps are shown, their check marks and their required dots.
  const layoutKey = steps.map((step) => `${step.key}:${step.complete ? 1 : 0}:${step.required ? 1 : 0}`).join('|');
  // Which steps show as icons only. None while the labelled bar fits; otherwise the fewest that make it fit, rightmost
  // first, never the current step (MDSI1-4952 D5).
  const { collapsed, widthRef, measureFullRef, measureCompactRef } = useStepBarMode({ stepCount: steps.length, currentIndex, layoutKey });

  const renderStep = (step: StepDefinition, iconOnly: boolean, forMeasure = false): JSX.Element => {
    const icon = <LottieIcon required={step.required} title={step.title} lottieFile={step.lottieFile} complete={step.complete} compact={iconOnly} placeholderIcon={forMeasure} />;
    let content = icon;

    if (iconOnly) {
      // No visible text, so the step name shows on hover, with the reason when the step is disabled. The measuring
      // copies skip the Tooltip (it adds no DOM of its own).
      const tooltipTitle = step.disabledReason ? (
        <>
          <div>
            <strong>{step.title}</strong>
          </div>
          <div>{step.disabledReason}</div>
        </>
      ) : (
        step.title
      );

      content = forMeasure ? (
        <Col>{icon}</Col>
      ) : (
        <Tooltip title={tooltipTitle}>
          <Col>{icon}</Col>
        </Tooltip>
      );
    } else if (step.wrapped) {
      // Same wrapper the labelled bar has always used for these steps.
      content = forMeasure ? (
        <Col>{icon}</Col>
      ) : (
        <Tooltip title={step.disabledReason}>
          <Col>{icon}</Col>
        </Tooltip>
      );
    }

    return (
      <Menu.Item style={stepItemStyle} className={iconOnly ? 'step-bar-icon-only' : undefined} disabled={step.disabled} key={step.key}>
        {content}
      </Menu.Item>
    );
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ position: 'relative', width: '100%' }}>
        {/* disabledOverflow: every step is always rendered, antd never folds steps into a "…" menu. */}
        <Menu onClick={deploymentRequestId ? handleNavigationEdit : handleNavigationCreate} mode="horizontal" disabledOverflow style={menuStyle} selectedKeys={[currentLocation]}>
          {steps.map((step, index) => renderStep(step, collapsed[index]))}
        </Menu>
        {/* Invisible copies of the bar, every step labelled and every step as an icon, used only to read each step's two
            widths. They never collapse, so their size changes only when the steps themselves do. */}
        <div ref={widthRef} aria-hidden="true" style={measureBoxStyle}>
          <div ref={measureFullRef} style={{ width: 'max-content' }}>
            <Menu mode="horizontal" disabledOverflow selectable={false} style={menuStyle}>
              {steps.map((step) => renderStep(step, false, true))}
            </Menu>
          </div>
          <div ref={measureCompactRef} style={{ width: 'max-content' }}>
            <Menu mode="horizontal" disabledOverflow selectable={false} style={menuStyle}>
              {steps.map((step) => renderStep(step, true, true))}
            </Menu>
          </div>
        </div>
      </div>
    </Space>
  );
};
